import { useState, useEffect } from "react";
import { fetchAdmin } from "../../utils/adminApi";
import { TrendingUp, Users, MapPin, DollarSign, Calendar, BarChart3 } from "lucide-react";

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const res = await fetchAdmin("/api/admin/analytics");
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" /></div>;
  if (!data) return <p className="text-stone-500 text-center py-8">Failed to load analytics</p>;

  const statCards = [
    { label: "Total Revenue", value: `$${(data.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: "text-emerald-400", bg: "bg-emerald-500/10" },
    { label: "Total Users", value: data.total_users || 0, icon: Users, color: "text-blue-400", bg: "bg-blue-500/10" },
    { label: "Total Bookings", value: data.total_bookings || 0, icon: MapPin, color: "text-amber-400", bg: "bg-amber-500/10" },
    { label: "Avg Booking Value", value: `$${(data.avg_booking_value || 0).toLocaleString()}`, icon: TrendingUp, color: "text-purple-400", bg: "bg-purple-500/10" },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      <h2 className="text-xl font-semibold text-white">Analytics Overview</h2>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map(s => (
          <div key={s.label} className={`${s.bg} border border-stone-800 rounded-xl p-5`} data-testid={`analytics-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <s.icon className={`w-6 h-6 ${s.color} mb-3`} />
            <p className="text-2xl font-bold text-white">{s.value}</p>
            <p className="text-stone-400 text-sm mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Booking Status Breakdown */}
      <div className="bg-stone-900/60 border border-stone-800 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-amber-400" />Booking Status Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(data.status_breakdown || {}).map(([status, count]) => (
            <div key={status} className="text-center">
              <p className="text-2xl font-bold text-white">{count}</p>
              <p className={`text-sm capitalize ${status === "confirmed" ? "text-emerald-400" : status === "completed" ? "text-blue-400" : status === "cancelled" ? "text-red-400" : "text-amber-400"}`}>{status}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Popular Programs */}
      {data.popular_programs?.length > 0 && (
        <div className="bg-stone-900/60 border border-stone-800 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-amber-400" />Top Programs</h3>
          <div className="space-y-3">
            {data.popular_programs.map((p, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-amber-600/20 text-amber-400 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <span className="text-white">{p.name}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-stone-400 text-sm">{p.bookings} bookings</span>
                  <span className="text-emerald-400 text-sm font-medium">${(p.revenue || 0).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Bookings */}
      {data.monthly_bookings?.length > 0 && (
        <div className="bg-stone-900/60 border border-stone-800 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-amber-400" />Monthly Bookings</h3>
          <div className="flex items-end gap-2 h-40">
            {data.monthly_bookings.map((m, i) => {
              const max = Math.max(...data.monthly_bookings.map(x => x.count), 1);
              const pct = (m.count / max) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-stone-400 text-xs">{m.count}</span>
                  <div className="w-full bg-amber-600/80 rounded-t-sm" style={{ height: `${Math.max(pct, 5)}%` }} />
                  <span className="text-stone-500 text-xs">{m.month}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {data.recent_contacts?.length > 0 && (
        <div className="bg-stone-900/60 border border-stone-800 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Recent Contact Inquiries</h3>
          <div className="space-y-2">
            {data.recent_contacts.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-stone-800 pb-2">
                <div><span className="text-white">{c.name}</span><span className="text-stone-500 ml-2">{c.email}</span></div>
                <span className="text-stone-400 text-xs">{c.tour || "General"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
